import RechargeCooldownLabel from "../../RechargeCooldownLabel";
import { RechargeCooldownError } from "@/store/rechargeCooldown";
import React, { useEffect, useRef, useState } from "react";
import "../flatRecharge.less";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useChangeFlatRecharge,
  useWalletAll,
  useWalletRechargeFlat,
} from "@/store/walletStore";
import { useUserInfo } from "@/store/userInfo";
import FormUI from '@/componentsUI/Form';
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import {
  IFWalletRechargeFlatReq,
} from "@/api/apiReq/IFWalletReq";
import FlatPix from "../pix/FlatPix";
import ButtonUI from "@/componentsUI/Button";
import { AmountMultiplierConversion } from "@/Core/utils/currencyDealCenter";
import useDialogManager from "@/utils/useDialogManager";
import DepositCurrency from "../compoments/depositCurrency/DepositCurrency";
import DepositWay from "../compoments/depositWay/DepositWay";
import PaymentConfirmModal from "../compoments/PaymentConfirmModal/PaymentConfirmModal";
import MobileInput from "../compoments/mobileInput/MobileInput";
import { getUserMobile } from "@/utils/mobileUtils";
import { useRechargeCurrency } from "../../useRechargeCurrency";
import { useRechargePaymentMethod } from "../useRechargePaymentMethod";
import { feedback } from "@/utils/feedback";

const FlatRechargeDefault = () => {
  const { t } = useTranslationPlus();
  const { data: wallets } = useWalletAll();
  const { data: userInfo } = useUserInfo();
  const [form] = FormUI.useForm();
  const { params, displayCurrencyType, changeRecharge } = useRechargeCurrency(wallets?.flat, form);
  const { isValidating, data, error, mutate } = useChangeFlatRecharge(params);
  const { trigger, isMutating, isRechargeBlocked } = useWalletRechargeFlat();
  const { openDialogIFrame } = useDialogManager();
  const formValidator = useRef(null);
  const { rule, changeType } = useRechargePaymentMethod(data?.payParams, form, params?.currencyType);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  useEffect(() => {
    setShowPaymentModal(false);
    setPendingFormValues(null);
    formValidator.current = false;
  }, [rule, params?.currencyType]);

  const isAutoTriggerPayMethod = (payMethod?: string): boolean => {
    if (!payMethod) return false;
    return payMethod.trim().toLowerCase() === "spei";
  };

  function recharge() {
    if (!rule || error || isValidating || isMutating || isRechargeBlocked) return;
    formValidator.current = true;
    form
      .validate()
      .then((v) => {
        const payMethod = v?.payType?.type;
        if (isAutoTriggerPayMethod(payMethod)) {
          formValidator.current = false;
          submitPayment(v);
          return;
        }
        setPendingFormValues(v);
        setShowPaymentModal(true);
        formValidator.current = false;
      })
      .catch(() => {
        formValidator.current = false;
      });
  }

  function submitPayment(values, verifyCode?: string) {
    if (!values || !rule || error || isValidating || isMutating || isRechargeBlocked) return;

    const selectedWallet = wallets?.flat?.find(
      (w) => w.currencyType === values?.walletType
    );

    const paymentData: IFWalletRechargeFlatReq = {
      currencyType: selectedWallet?.serverCurrencyType || values?.walletType,
      money: AmountMultiplierConversion(values?.amount, {
        currencyType: selectedWallet?.currencyType,
        serverCurrencyType: selectedWallet?.serverCurrencyType,
      }),
      payCode: values?.payType?.data?.payCode,
      mobile: values?.mobile,
      rechargeMobileUserId: userInfo?.userId,
    };

    if (verifyCode) {
      paymentData.verifyCode = verifyCode;
    }

    trigger(paymentData)
      .then((res) => {
        setShowPaymentModal(false);
        setPendingFormValues(null);

        if (res?.payUrl) {
          openDialogIFrame({
            title: t("Wallet.Wallet_PayUrlTitle"),
            url: res?.payUrl,
            type: "pay",
          });
        } else {
          feedback.success(t("Wallet.Payment request submitted successfully"));
        }
      })
      .catch((e) => {
        if (e instanceof RechargeCooldownError) return;
        feedback.error(t(e?.code));
        setShowPaymentModal(false);
        setPendingFormValues(null);
      });
  }

  function handlePaymentConfirm(verifyCode?: string) {
    if (!pendingFormValues) return;
    submitPayment(pendingFormValues, verifyCode);
  }

  function handlePaymentModalClose() {
    setShowPaymentModal(false);
    setPendingFormValues(null);
  }

  useEffect(() => {
    const mobile = getUserMobile(userInfo?.mobile, userInfo?.userId);
    if (mobile) {
      form.setFieldValue("mobile", mobile);
    }
  }, [userInfo]);

  useEffect(() => {
    if (error) {
      console.error("Flat recharge error:", {
        error,
        errorCode: error?.code,
        errorMessage: error?.message,
        params,
      });
    }
  }, [error, params]);

  return (
    <div className="flat-Recharge">
      <FormUI form={form} layout={"vertical"} autoComplete={"off"}>
        <LoadingUI
          isLoading={isValidating}
          error={error}
          dataList={data}
          retryFunc={mutate}
        >
          <div className="deposit-row">
            <FromItemPlus
              field={"walletType"}
              label={t("Wallet.Currency")}
              className="deposit-currency-item"
            >
              <DepositCurrency
                currencys={wallets?.flat}
                onChange={(_value: any, option: any) => {
                  changeRecharge(option?._index);
                }}
              />
            </FromItemPlus>
          </div>
          <div className="deposit-method-section">
            <FromItemPlus
              field={"payType"}
              label={t("Wallet.Method")}
              className="deposit-way-item"
              shouldUpdate
            >
              {(values) => (
                <DepositWay
                  payParams={data?.payParams}
                  onChange={changeType}
                  value={values?.payType}
                />
              )}
            </FromItemPlus>
          </div>
          <div className="deposit-mobile-section">
            <FromItemPlus
              field={"mobile"}
              label={t("Wallet.Mobile Number")}
              className="deposit-mobile-item"
              rules={[
                {
                  required: true,
                  message: t("Wallet.Please enter mobile number"),
                },
                {
                  validator: (value, cb) => {
                    if (value && (value.length < 8 || value.length > 10)) {
                      return cb(t("Wallet.Mobile number must be"));
                    }
                    return cb();
                  },
                },
              ]}
            >
              <MobileInput />
            </FromItemPlus>
            <div className="important-notice">
              {t("Wallet.DepositPhoneNotice")}
            </div>
          </div>
          <FlatPix
            data={data}
            form={form}
            currencyType={displayCurrencyType}
            serverCurrencyType={params?.currencyType}
            formValidator={formValidator}
            rule={rule}
          />
        </LoadingUI>
      </FormUI>
      <ButtonUI
        className={`recharge-btn`}
        data-recharge-spin-entry-anchor="deposit-submit"
        type="primary"
        loading={isMutating}
        disabled={!rule || !!error || isValidating || isMutating || isRechargeBlocked}
        onClick={recharge}
      >
        <RechargeCooldownLabel>{t("Wallet.Deposit")}</RechargeCooldownLabel>
      </ButtonUI>

      <PaymentConfirmModal
        disabled={isValidating || !!error || !rule || isRechargeBlocked}
        visible={showPaymentModal}
        onClose={handlePaymentModalClose}
        onConfirm={handlePaymentConfirm}
        paymentMethod={pendingFormValues?.payType?.type || ""}
        loading={isMutating}
      />
    </div>
  );
};

export default FlatRechargeDefault;
